import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { getFieldErrors, IFieldError } from "../helpers";
import { IRentalItem } from "./rentalItemsReducer";

export interface ICartItem {
  rentalItem: IRentalItem;
  count: number;
}

export interface ICart {
  items: ICartItem[];
}

const initialCart: ICart = localStorage.getItem("cart")
  ? JSON.parse(localStorage?.cart ?? "{items: []}")
  : { items: [] };

class PlaceOrderErrs {
  form: string | null = null;
  fullName: string | null = null;
  phoneNumber: string | null = null;
}

export interface ICartState {
  cart: ICart;
  isPlacingOrder: boolean;
  isOrderPlaced: boolean;
  placeOrderErrs: PlaceOrderErrs;
}

const initialState = {
  cart: initialCart,
  isPlacingOrder: false,
  isOrderPlaced: false,
  placeOrderErrs: {
    form: null,
    fullName: null,
    phoneNumber: null,
  },
} as ICartState;

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    /**
     * Adds the item to the cart. It the cart already has an item with the same rental-item,
     * the quantity is only increased by the amount of the passed cart-item, rather than
     * adding a new one
     *
     * @param state
     * @param action
     */
    addItemToCart: (state: ICartState, action: PayloadAction<ICartItem>) => {
      const itemToAdd: ICartItem = action.payload;

      const items: ICartItem[] = state.cart.items;
      let existingItem: ICartItem | null = null;

      for (let i = 0; i < items.length; i++) {
        if (items[i].rentalItem._id === itemToAdd.rentalItem._id) {
          items[i].count += itemToAdd.count;

          existingItem = items[i];
          break;
        }
      }

      if (!existingItem) {
        items.push(itemToAdd);
      }

      state.cart.items = items;

      localStorage.setItem("cart", JSON.stringify(state.cart));
    },

    /**
     * Decrements the quantity of an item(using the passed the rental-item-name) by the passed amount(the passed count)
     * If count is 2, the quantity of the item is reduced by 2
     * If count is greater >= to the count of the item, the item is removed from the cart
     *
     * @param state
     * @param action
     */
    decrementCartItemCount: (
      state: ICartState,
      action: PayloadAction<{ rentalItemId: string; count?: number }>
    ) => {
      const { count = 1, rentalItemId } = action.payload;
      let items: ICartItem[] = state.cart.items;

      //The way the item was before this updat
      let oldItem: ICartItem | null = null;

      for (let i = 0; i < items.length; i++) {
        if (items[i].rentalItem._id === rentalItemId) {
          oldItem = items[i];
          items[i].count -= count;
          break;
        }
      }

      if (oldItem && count > oldItem.count) {
        items = items.filter((item) => item.rentalItem._id !== rentalItemId);
      }

      state.cart.items = items;

      localStorage.setItem("cart", JSON.stringify(state.cart));
    },

    /**
     * removes all ocurrences of that rental-item from the cart
     * @param state
     * @returns
     */
    removeItemByRentalItemId: (
      state: ICartState,
      action: PayloadAction<string>
    ) => {
      state.cart.items = state.cart.items.filter(
        (item) => item.rentalItem._id !== action.payload
      );

      localStorage.setItem("cart", JSON.stringify(state.cart));
    },

    /**
     * This empties the cart
     * @param state
     */
    clearCart: (state: ICartState) => {
      state.cart = { items: [] };
      localStorage.setItem("cart", JSON.stringify(state.cart));
    },

    placeOrderStart: (state: ICartState) => {
      state.placeOrderErrs = {
        form: null,
        fullName: null,
        phoneNumber: null,
      };
      state.isOrderPlaced = false;
      state.isPlacingOrder = true;
    },

    placeOrderPass: (state: ICartState) => {
      state.isOrderPlaced = true;
      state.isPlacingOrder = false;
    },

    placeOrderFail: (
      state: ICartState,
      action: PayloadAction<IFieldError[]>
    ) => {
      state.isPlacingOrder = false;

      const errs = action.payload;

      for (const err of errs) {
        for (const key in state.placeOrderErrs) {
          if (err.field === key) {
            state.placeOrderErrs[key as keyof PlaceOrderErrs] = err.msg;
            break;
          }
        }
      }

      if (state?.placeOrderErrs && !state.placeOrderErrs.form) {
        state.placeOrderErrs.form = "Form incorrectly filled.";
      }
    },
    clearPlaceOrderErr: (state: ICartState, action: PayloadAction<string>) => {
      state.placeOrderErrs[action.payload as keyof PlaceOrderErrs] = null;
    },
    tryResetPlaceOrderFormErr: (state: ICartState) => {
      let shouldReset = true;

      for (let key in state.placeOrderErrs) {
        if (key === "form") {
          continue;
        }

        if (state.placeOrderErrs[key as keyof PlaceOrderErrs]) {
          shouldReset = false;
          break;
        }
      }

      if (shouldReset) {
        state.placeOrderErrs.form = null;
      }
    },
    resetOrderPlacement: (state: ICartState) => {
      state.isPlacingOrder = false;
      state.isOrderPlaced = false;
      state.placeOrderErrs = {
        form: null,
        fullName: null,
        phoneNumber: null,
      };
    },
  },
});

export const {
  addItemToCart,
  decrementCartItemCount,
  removeItemByRentalItemId,
  clearCart,
  placeOrderStart,
  placeOrderPass,
  placeOrderFail,
  clearPlaceOrderErr,
  tryResetPlaceOrderFormErr,
  resetOrderPlacement,
} = cartSlice.actions;

interface ITryPlaceOrderArg {
  fullName: string;
  phoneNumber: string;
  cart: ICart;
}

export const tryPlaceOrder = createAsyncThunk(
  "companies/ITryPlaceOrder",
  (arg: ITryPlaceOrderArg, { dispatch }) => {
    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/cart/place-order`;

    const {} = arg;

    dispatch(placeOrderStart());

    axios
      .post(endpoint, arg, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then((res) => {
        dispatch(placeOrderPass());
      })
      .catch((err) => {
        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
          fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
          fieldErrors = [{ field: "form", msg: data.err }];
        } else {
          fieldErrors = [
            { field: "form", msg: "Check your network connection." },
          ];
        }

        dispatch(placeOrderFail(fieldErrors));
      });
  }
);

export default cartSlice.reducer;
